"use client";

import { useEffect, useRef, useState } from "react";


const contactNumbers = [
  { sport: "Tennis", phone: "+91 93822 22220", icon: "🎾" },
  { sport: "Pickleball", phone: "+91 93822 22220", icon: "🏓" },
  { sport: "Swimming", phone: "+91 98861 63131", icon: "🏊" },
  { sport: "Fitness Studio", phone: "+91 96603 61188", icon: "💪" },
];

export default function ContactSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", sport: "", message: "" });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    const section = sectionRef.current;
    if (section) observer.observe(section);
    return () => { if (section) observer.unobserve(section); };
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Connect to backend/email service
    setSubmitted(true);
  };

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="reveal relative py-16 sm:py-20 md:py-28"
      style={{ background: "var(--navy-mid)" }}
    >
      {/* Background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse at 0% 50%, rgba(224,96,49,0.05) 0%, transparent 50%)",
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="section-label mb-4">Get in Touch</p>
          <h2 className="headline-lg text-white">
            Start Your{" "}
            <span className="gradient-text">Journey.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left: Info */}
          <div className="space-y-8">
            {/* Location */}
            <div
              className="contact-card glass-card p-7 space-y-4"
              style={{ border: "1px solid rgba(255,255,255,0.07)" }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: "rgba(224,96,49,0.15)" }}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="var(--terracotta)" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <div className="text-white font-bold">Location</div>
                  <div className="text-white/50 text-sm">Doddajala, Bengaluru, Karnataka 562157</div>
                </div>
              </div>
            </div>

            {/* Email */}
            <div
              className="contact-card glass-card p-7"
              style={{ border: "1px solid rgba(255,255,255,0.07)" }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: "rgba(224,96,49,0.15)" }}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="var(--terracotta)" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <div className="text-white font-bold">Email</div>
                  <a href="mailto:official.playxp@gmail.com" className="text-sm transition-colors" style={{ color: "var(--terracotta)" }}>
                    official.playxp@gmail.com
                  </a>
                </div>
              </div>
            </div>

            {/* Hours */}
            <div
              className="contact-card glass-card p-7"
              style={{ border: "1px solid rgba(255,255,255,0.07)" }}
            >
              <div className="flex items-center gap-3 mb-5">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: "rgba(224,96,49,0.15)" }}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="var(--terracotta)" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="text-white font-bold">Opening Hours</div>
              </div>
              <div className="space-y-2 pl-0 sm:pl-12">
                <div className="flex justify-between text-sm">
                  <span className="text-white/60">Mon – Fri</span>
                  <span className="text-white font-semibold">6:00 am – 6:00 pm</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/60">Sat – Sun</span>
                  <span className="text-white font-semibold">7:00 am – 5:00 pm</span>
                </div>
              </div>
            </div>

            {/* Sport-specific contacts */}
            <div
              className="contact-card glass-card p-7"
              style={{ border: "1px solid rgba(255,255,255,0.07)" }}
            >
              <div className="text-white font-bold mb-5">Sport Enquiries</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {contactNumbers.map((c) => (
                  <a
                    key={c.sport}
                    href={`tel:${c.phone.replace(/\s/g, "")}`}
                    className="flex items-center gap-3 p-3 rounded-xl transition-all duration-300 hover:bg-white/[0.06] group"
                  >
                    <span className="text-xl">{c.icon}</span>
                    <div>
                      <div className="text-white/70 text-xs font-bold uppercase tracking-wide group-hover:text-white transition-colors">
                        {c.sport}
                      </div>
                      <div className="text-white/50 text-xs group-hover:text-terracotta transition-colors" style={{ color: "rgba(255,255,255,0.4)" }}>
                        {c.phone}
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div
            className="glass-card p-8 md:p-10"
            style={{ border: "1px solid rgba(255,255,255,0.07)" }}
          >
            {submitted ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-16 space-y-6">
                <div
                  className="w-20 h-20 rounded-full flex items-center justify-center"
                  style={{ background: "rgba(224,96,49,0.15)", border: "2px solid var(--terracotta)" }}
                >
                  <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="var(--terracotta)" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-white">We'll Be in Touch!</h3>
                <p className="text-white/60">Our team will reach out within 24 hours to get you started at Play.XP.</p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="btn-secondary"
                >
                  <span>Send Another Message</span>
                </button>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-bold text-white mb-2">Book a Session</h3>
                <p className="text-white/50 text-sm mb-8">Fill in your details and we'll get you on the courts.</p>

                <form onSubmit={handleSubmit} className="space-y-5" suppressHydrationWarning>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-widest text-white/50 mb-2">Name</label>
                      <input
                        type="text"
                        required
                        placeholder="Arjun Mehta"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl text-white text-sm font-medium placeholder-white/25 focus:outline-none transition-all duration-300"
                        style={{
                          background: "rgba(255,255,255,0.06)",
                          border: "1px solid rgba(255,255,255,0.1)",
                        }}
                        onFocus={(e) => { e.target.style.borderColor = "rgba(224,96,49,0.5)"; e.target.style.background = "rgba(255,255,255,0.08)"; }}
                        onBlur={(e) => { e.target.style.borderColor = "rgba(255,255,255,0.1)"; e.target.style.background = "rgba(255,255,255,0.06)"; }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-widest text-white/50 mb-2">Phone</label>
                      <input
                        type="tel"
                        placeholder="+91 98765 43210"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl text-white text-sm font-medium placeholder-white/25 focus:outline-none transition-all duration-300"
                        style={{
                          background: "rgba(255,255,255,0.06)",
                          border: "1px solid rgba(255,255,255,0.1)",
                        }}
                        onFocus={(e) => { e.target.style.borderColor = "rgba(224,96,49,0.5)"; e.target.style.background = "rgba(255,255,255,0.08)"; }}
                        onBlur={(e) => { e.target.style.borderColor = "rgba(255,255,255,0.1)"; e.target.style.background = "rgba(255,255,255,0.06)"; }}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-white/50 mb-2">Email</label>
                    <input
                      type="email"
                      required
                      placeholder="arjun@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl text-white text-sm font-medium placeholder-white/25 focus:outline-none transition-all duration-300"
                      style={{
                        background: "rgba(255,255,255,0.06)",
                        border: "1px solid rgba(255,255,255,0.1)",
                      }}
                      onFocus={(e) => { e.target.style.borderColor = "rgba(224,96,49,0.5)"; e.target.style.background = "rgba(255,255,255,0.08)"; }}
                      onBlur={(e) => { e.target.style.borderColor = "rgba(255,255,255,0.1)"; e.target.style.background = "rgba(255,255,255,0.06)"; }}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-white/50 mb-2">Interested In</label>
                    <select
                      value={formData.sport}
                      onChange={(e) => setFormData({ ...formData, sport: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl text-sm font-medium focus:outline-none transition-all duration-300"
                      style={{
                        background: "rgba(255,255,255,0.06)",
                        border: "1px solid rgba(255,255,255,0.1)",
                        color: formData.sport ? "white" : "rgba(255,255,255,0.25)",
                      }}
                      onFocus={(e) => { e.target.style.borderColor = "rgba(224,96,49,0.5)"; }}
                      onBlur={(e) => { e.target.style.borderColor = "rgba(255,255,255,0.1)"; }}
                    >
                      <option value="" style={{ background: "#1B365D" }}>Select a sport or facility</option>
                      <option value="tennis" style={{ background: "#1B365D" }}>Tennis</option>
                      <option value="pickleball" style={{ background: "#1B365D" }}>Pickleball</option>
                      <option value="swimming" style={{ background: "#1B365D" }}>Swimming (Riverpool)</option>
                      <option value="fitness" style={{ background: "#1B365D" }}>Fitness Studio</option>
                      <option value="membership" style={{ background: "#1B365D" }}>Full Membership</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-white/50 mb-2">Message (Optional)</label>
                    <textarea
                      rows={4}
                      placeholder="Tell us about your fitness goals or what you're looking for..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl text-white text-sm font-medium placeholder-white/25 focus:outline-none transition-all duration-300 resize-none"
                      style={{
                        background: "rgba(255,255,255,0.06)",
                        border: "1px solid rgba(255,255,255,0.1)",
                      }}
                      onFocus={(e) => { e.target.style.borderColor = "rgba(224,96,49,0.5)"; e.target.style.background = "rgba(255,255,255,0.08)"; }}
                      onBlur={(e) => { e.target.style.borderColor = "rgba(255,255,255,0.1)"; e.target.style.background = "rgba(255,255,255,0.06)"; }}
                    />
                  </div>

                  <button type="submit" className="btn-primary w-full justify-center text-base py-4">
                    <span>Send Enquiry</span>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                    </svg>
                  </button>
                </form>
              </>
            )}
          </div>
        </div>

        {/* Map embed placeholder */}
        <div
          className="mt-12 rounded-2xl overflow-hidden relative"
          style={{ height: "300px", border: "1px solid rgba(255,255,255,0.07)" }}
        >
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3884.5!2d77.64824228465542!3d13.184877028333721!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTPCsDExJzA1LjYiTiA3N8KwMzknMDAuOSJF!5e0!3m2!1sen!2sin!4v1709000000000!5m2!1sen!2sin"
            width="100%"
            height="100%"
            style={{ border: 0, filter: "invert(90%) hue-rotate(180deg) brightness(0.8) contrast(1.1)" }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Play.XP location map - Doddajala, Bengaluru, Karnataka"
          />
        </div>
      </div>
    </section>
  );
}